// Fabric rendering adapter for the portable text layout engine.
//
// This is the ONLY place allowed to know about fabric's internal text
// constants (_fontSizeMult, _fontSizeFraction, lineHeight). It translates a
// layout result - which is expressed purely in em multiples and box-relative
// fractions - into the fontSize / width / left / top / angle values fabric
// needs so the ink lands exactly where the engine said it should. A future
// ImageSharp renderer is a sibling of this file consuming the same result.
//
// Fabric geometry facts this file relies on, verified against fabric v6:
//   heightOfLine   = fontSize * lineHeight * _fontSizeMult
//   objectHeight   = (lineCount - 1) * heightOfLine + fontSize * _fontSizeMult
//   firstBaseline  = -objectHeight / 2 + fontSize * _fontSizeMult * (1 - _fontSizeFraction)
//   linePenX       = -objectWidth / 2 + lineLeftOffset(textAlign, lineAdvance)
//
// fabric only exposes those constants and its resolved lines as underscored
// members, hence the rule exemption below.
/* eslint-disable no-underscore-dangle */

import * as fabric from 'fabric';
import textMetricsProvider from './textMetricsProvider';

// dev-only instrumentation: draws the container box, the resolved ink box and
// each baseline. Never ship this enabled.
const DEBUG_TEXT_LAYOUT = false;

const DEBUG_CONTAINER_COLOR = '#00e0ff';
const DEBUG_INK_COLOR = '#ff00a0';
const DEBUG_BASELINE_COLOR = '#00c853';

const MIN_FABRIC_TEXTBOX_WIDTH = 2;
const SINGLE_LINE_WIDTH_MARGIN = 2;

// fabric sums per-grapheme widths where the engine measures the whole string,
// so the two disagree by a rounding-scale amount. Handing fabric the engine's
// own wrap width plus this hair of slack keeps a borderline word from flipping
// onto the next line; the slack is dropped again if it lets fabric re-join a
// line the engine split.
const WRAP_WIDTH_EPSILON = 1;

const degreesToRadians = (degrees) => ((degrees || 0) * Math.PI) / 180;

const getBoxSize = (container) => ({
  width: container.width * container.scaleX,
  height: container.height * container.scaleY
});

// the engine derives line spacing from the font itself, so nothing about
// fabric's own line height feeds into the layout any more - it is an output of
// the engine that this adapter applies, not an input it reads.
const getFabricLayoutOptions = () => ({});

// fabric spaces lines at fontSize * lineHeight * _fontSizeMult, so this is the
// lineHeight that reproduces the engine's spacing exactly
const toFabricLineHeight = (textObj, lineSpacingEm) =>
  lineSpacingEm / textObj._fontSizeMult;

// fabric's resolved lines as plain strings; _textLines holds one grapheme
// array per rendered line
const getRenderedLineText = (textObj, index) => {
  const graphemes = textObj._textLines[index];
  return Array.isArray(graphemes) ? graphemes.join('') : `${graphemes ?? ''}`;
};

// line count alone is not enough: fabric can move a break while keeping the
// same number of lines, and then per-line widths would be paired with engine
// metrics for different text. Only an exact content match (modulo the edge
// whitespace fabric drops at wrap points) lets fabric geometry be trusted.
const renderedLinesMatchLayout = (textObj, layout) => {
  const renderedLines = textObj._textLines;

  if (
    !Array.isArray(renderedLines) ||
    renderedLines.length !== layout.lines.length
  ) {
    return false;
  }

  return layout.lines.every(
    (line, index) => getRenderedLineText(textObj, index).trim() === line.trim()
  );
};

const getRenderedLineAdvances = (textObj, layout) => {
  if (!renderedLinesMatchLayout(textObj, layout)) {
    return layout.lineAdvances.map((advanceEm) => advanceEm * layout.fontSize);
  }

  return layout.lines.map((line, index) => textObj.getLineWidth(index));
};

const getLinePenOffset = (textAlign, objectWidth, lineAdvance) => {
  if (textAlign === 'center') {
    return -lineAdvance / 2;
  }
  if (textAlign === 'right') {
    return objectWidth / 2 - lineAdvance;
  }
  return -objectWidth / 2;
};

const buildResolvedLayout = (textObj, layout, boxSize) => {
  const fontWeight = textObj.fontWeight || 'normal';
  const fontStyle = textObj.fontStyle || 'normal';

  // font readiness at stamp time is the authority here: the object's own flag
  // is only set after this layout has been built, so on a first pass it is
  // still undefined while the layout may well have been measured against the
  // fallback face. isFontReady answers true when it cannot tell, so the flag
  // remains the fallback signal in that case.
  const isFontReady = textMetricsProvider.isFontReady({
    fontFamily: textObj.fontFamily,
    fontStyle,
    fontWeight
  });

  return {
    version: layout.version,
    fontSizePx: layout.fontSize,
    lines: layout.lines,
    baselines: layout.baselines,
    lineInkLeft: layout.lineInkLeft,
    lineSpacingEm: layout.lineSpacingEm,
    inkBox: layout.inkBox,
    fittedAxis: layout.fittedAxis,
    overflow: layout.overflow,
    fitMode: layout.fitMode,
    textAlign: layout.textAlign,
    safetyInsetX: layout.insetX,
    safetyInsetY: layout.insetY,
    boxWidthPx: boxSize.width,
    boxHeightPx: boxSize.height,
    fontKey: `${textObj.fontFamily}|${fontWeight}|${fontStyle}`,
    measuredWith: 'browser-canvas2d',
    isProvisional: !isFontReady || !!textObj.isProvisionalTextLayout
  };
};

const clearTextLayoutDebug = (textObj) => {
  const { canvas } = textObj;
  if (!canvas || !textObj.textLayoutDebugObjects) {
    return;
  }

  textObj.textLayoutDebugObjects.forEach((obj) => canvas.remove(obj));
  textObj.set({ textLayoutDebugObjects: null });
};

const renderTextLayoutDebug = (textObj, container, layout) => {
  clearTextLayoutDebug(textObj);

  const { canvas } = textObj;
  if (!canvas || !layout) {
    return;
  }

  const boxSize = getBoxSize(container);
  const radians = degreesToRadians(container.angle);
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);

  const toScenePoint = (localX, localY) => ({
    left: container.left + localX * cos - localY * sin,
    top: container.top + localX * sin + localY * cos
  });

  const debugDefaults = {
    originX: 'center',
    originY: 'center',
    angle: container.angle,
    fill: 'transparent',
    selectable: false,
    evented: false,
    excludeFromExport: true,
    objectCaching: false
  };

  const inkCenter = toScenePoint(
    (layout.inkBox.left + layout.inkBox.width / 2) * boxSize.width -
      boxSize.width / 2,
    (layout.inkBox.top + layout.inkBox.height / 2) * boxSize.height -
      boxSize.height / 2
  );

  const debugObjects = [
    new fabric.Rect({
      ...debugDefaults,
      ...toScenePoint(0, 0),
      width: boxSize.width,
      height: boxSize.height,
      stroke: DEBUG_CONTAINER_COLOR,
      strokeWidth: 1
    }),
    new fabric.Rect({
      ...debugDefaults,
      ...inkCenter,
      width: layout.inkBox.width * boxSize.width,
      height: layout.inkBox.height * boxSize.height,
      stroke: DEBUG_INK_COLOR,
      strokeWidth: 1
    }),
    ...layout.baselines.map(
      (baseline) =>
        new fabric.Rect({
          ...debugDefaults,
          ...toScenePoint(0, baseline * boxSize.height - boxSize.height / 2),
          width: boxSize.width,
          height: 1,
          fill: DEBUG_BASELINE_COLOR
        })
    )
  ];

  debugObjects.forEach((obj) => canvas.add(obj));
  textObj.set({ textLayoutDebugObjects: debugObjects });
};

const positionFabricTextbox = (textObj, container, offsetX, offsetY) => {
  const angle = container.angle || 0;
  const radians = degreesToRadians(angle);
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);

  textObj.set({
    left: container.left + offsetX * cos - offsetY * sin,
    top: container.top + offsetX * sin + offsetY * cos,
    angle,
    scaleX: 1,
    scaleY: 1
  });
  textObj.setCoords();
};

// re-applies the last resolved ink offset without re-running the fit; used
// while the container is being dragged or rotated, and when there is nothing
// to measure so the previous size has to be kept
const repositionFabricTextbox = (textObj, container) => {
  const offset = textObj.inkLayoutOffset || { x: 0, y: 0 };
  positionFabricTextbox(textObj, container, offset.x, offset.y);
};

const applyLayoutToFabricTextbox = (textObj, container, layout) => {
  const boxSize = getBoxSize(container);
  const { fontSize } = layout;

  // 1. width and size first, so fabric can never re-break what the engine
  //    already decided. A wrapped block gets the wrap width the engine's own
  //    greedy wrapper ran at, which reproduces exactly those breaks; a resolved
  //    single line, and the paths whose wrap width cannot re-break (multiline
  //    off, hard breaks only), get a generous width so fabric cannot wrap at
  //    all.
  textObj.set({ centeredScaling: true });

  const widestAdvancePx = Math.max(...layout.lineAdvances, 0) * fontSize;
  const widthCandidates =
    layout.lines.length > 1 && Number.isFinite(layout.wrapWidthEm)
      ? [
          layout.wrapWidthEm * fontSize + WRAP_WIDTH_EPSILON,
          layout.wrapWidthEm * fontSize
        ]
      : [Math.max(widestAdvancePx + SINGLE_LINE_WIDTH_MARGIN, boxSize.width)];

  for (let index = 0; index < widthCandidates.length; index += 1) {
    textObj.set({
      width: Math.max(widthCandidates[index], MIN_FABRIC_TEXTBOX_WIDTH),
      fontSize,
      lineHeight: toFabricLineHeight(textObj, layout.lineSpacingEm)
    });
    textObj.initDimensions();

    if (renderedLinesMatchLayout(textObj, layout)) {
      break;
    }
  }

  // 2. translate the desired ink placement into fabric's left / top. Read the
  //    rendered width back because fabric widens a textbox to fit an
  //    unbreakable word.
  const renderedWidth = textObj.width;
  const lineAdvances = getRenderedLineAdvances(textObj, layout);

  const lineMismatch = !renderedLinesMatchLayout(textObj, layout);

  if (DEBUG_TEXT_LAYOUT && lineMismatch) {
    // eslint-disable-next-line no-console
    console.warn('text layout: fabric re-broke the engine lines', {
      engineLines: layout.lines,
      fabricLines: textObj._textLines
    });
  }

  // fabric's rendered height reflects fabric's own wrapping; when that still
  // disagrees with the engine's lines - a different count, or the same count
  // with a shifted break - trusting fabric geometry would misplace the block,
  // so the height is rebuilt from the engine's line count with fabric's own
  // geometry - the Y axis twin of getRenderedLineAdvances' fallback
  const renderedHeight = lineMismatch
    ? (layout.lines.length - 1) *
        fontSize *
        textObj.lineHeight *
        textObj._fontSizeMult +
      fontSize * textObj._fontSizeMult
    : textObj.height;

  const inkEdges = layout.lineMetrics.map((metrics, index) => {
    const penX = getLinePenOffset(
      layout.textAlign,
      renderedWidth,
      lineAdvances[index]
    );

    return {
      left: penX - metrics.inkLeftEm * fontSize,
      right: penX + metrics.inkRightEm * fontSize
    };
  });

  const renderedInkLeft = Math.min(...inkEdges.map((edge) => edge.left));
  const renderedInkRight = Math.max(...inkEdges.map((edge) => edge.right));

  const desiredInkLeft = layout.inkBox.left * boxSize.width - boxSize.width / 2;
  const desiredInkRight = desiredInkLeft + layout.inkBox.width * boxSize.width;

  let offsetX;
  if (layout.textAlign === 'left') {
    offsetX = desiredInkLeft - renderedInkLeft;
  }
  else if (layout.textAlign === 'right') {
    offsetX = desiredInkRight - renderedInkRight;
  }
  else {
    offsetX =
      (desiredInkLeft + desiredInkRight) / 2 -
      (renderedInkLeft + renderedInkRight) / 2;
  }

  const renderedFirstBaseline =
    -renderedHeight / 2 +
    fontSize * textObj._fontSizeMult * (1 - textObj._fontSizeFraction);
  const desiredFirstBaseline =
    layout.baselines[0] * boxSize.height - boxSize.height / 2;
  const offsetY = desiredFirstBaseline - renderedFirstBaseline;

  const resolvedLayout = buildResolvedLayout(textObj, layout, boxSize);

  // the offset lives only on the text object; it is never written back to the
  // container, which keeps owning position and geometry
  textObj.set({ inkLayoutOffset: { x: offsetX, y: offsetY }, resolvedLayout });
  positionFabricTextbox(textObj, container, offsetX, offsetY);

  if (DEBUG_TEXT_LAYOUT) {
    renderTextLayoutDebug(textObj, container, layout);
  }

  return resolvedLayout;
};

export {
  applyLayoutToFabricTextbox,
  repositionFabricTextbox,
  getFabricLayoutOptions,
  clearTextLayoutDebug,
  DEBUG_TEXT_LAYOUT
};
